import { useState, useEffect } from 'react';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import axios from 'axios';
import { MapPin, Zap } from 'lucide-react';

const API = "/api";

const defaultProjects = [
  {
    id: 1,
    title: "Residential Rooftop Solar",
    description: "Complete 5kW rooftop solar installation for a family home, reducing electricity bills by 80%.",
    category: "residential",
    location: "Nagpur, Maharashtra",
    capacity: "5 kW",
    completion_date: "March 2024",
    image_url: "https://images.unsplash.com/photo-1508514177221-188b1cf16e9d?w=600&h=400&fit=crop"
  },
  {
    id: 2,
    title: "Commercial Office Complex",
    description: "50kW solar panel system installed on a multi-storey office building, powering all operations.",
    category: "commercial",
    location: "Pune, Maharashtra",
    capacity: "50 kW",
    completion_date: "June 2024",
    image_url: "https://images.unsplash.com/photo-1497440001374-f26997328c1b?w=600&h=400&fit=crop"
  },
  {
    id: 3,
    title: "Industrial Solar Farm",
    description: "Large-scale 200kW ground-mounted solar farm supplying power to a manufacturing plant.",
    category: "industrial",
    location: "Aurangabad, Maharashtra",
    capacity: "200 kW",
    completion_date: "September 2024",
    image_url: "https://images.unsplash.com/photo-1466611653911-95081537e5b7?w=600&h=400&fit=crop"
  },
  {
    id: 4,
    title: "Residential Society Installation",
    description: "15kW shared solar system installed for a housing society of 20 flats.",
    category: "residential",
    location: "Nagpur, Maharashtra",
    capacity: "15 kW",
    completion_date: "November 2024",
    image_url: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&h=400&fit=crop"
  },
  {
    id: 5,
    title: "Shopping Mall Rooftop",
    description: "75kW solar installation on a shopping mall rooftop, cutting commercial energy costs significantly.",
    category: "commercial",
    location: "Amravati, Maharashtra",
    capacity: "75 kW",
    completion_date: "January 2025",
    image_url: "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=600&h=400&fit=crop"
  },
  {
    id: 6,
    title: "Industrial Warehouse Solar",
    description: "100kW solar system for a large warehouse, enabling full daytime energy independence.",
    category: "industrial",
    location: "Nashik, Maharashtra",
    capacity: "100 kW",
    completion_date: "February 2025",
    image_url: "https://images.unsplash.com/photo-1509391366360-2e959784a276?w=600&h=400&fit=crop"
  }
];

const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchProjects();
  }, []);

  // ✅ SAFE FETCH
const fetchProjects = async () => {
  try {
    const response = await axios.get(`${API}/projects`);

    console.log("API response:", response.data);

    // ✅ Ensure it's always array
    if (Array.isArray(response.data) && response.data.length > 0) {
      setProjects(response.data);
    } else {
      setProjects(defaultProjects);
    }

  } catch (error) {
    console.error('Error fetching projects:', error);
    setProjects(defaultProjects); // fallback
  } finally {
    setLoading(false);
  }
};

 // ✅ prevent crash
const safeProjects = Array.isArray(projects) ? projects : [];

const filteredProjects =
  filter === 'all'
    ? safeProjects
    : safeProjects.filter((p) => p.category === filter);

  const categories = [
    { value: 'all', label: 'All Projects' },
    { value: 'residential', label: 'Residential' },
    { value: 'commercial', label: 'Commercial' },
    { value: 'industrial', label: 'Industrial' },
  ];

  return (
    <div className="min-h-screen" data-testid="projects-page">
      <Navbar />

      {/* Hero Section */}
      <section
        className="pt-32 pb-8 bg-gradient-to-br from-primary/5 via-transparent to-secondary/5"
        data-testid="projects-hero"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-heading font-bold text-5xl md:text-7xl text-primary mb-6 tracking-tight">
            Our Projects
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            Explore our portfolio of successful solar installations across residential, commercial, and industrial sectors.
          </p>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-8 border-b border-border" data-testid="projects-filter">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-4 justify-center">
            {categories.map((category) => (
              <button
                key={category.value}
                onClick={() => setFilter(category.value)}
                data-testid={`filter-${category.value}`}
                className={`px-6 py-3 rounded-full font-medium transition-all ${
                  filter === category.value
                    ? 'bg-primary text-primary-foreground shadow-lg'
                    : 'bg-muted text-foreground hover:bg-muted/70'
                }`}
              >
                {category.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-8" data-testid="projects-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-20">
              <div className="inline-block w-12 h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin"></div>
              <p className="mt-4 text-muted-foreground">Loading projects...</p>
            </div>
          ) : filteredProjects.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredProjects.map((project) => (
                <div
                  key={project.id}
                  data-testid={`project-card-${project.id}`}
                  className="bg-card border border-border/50 shadow-sm hover:shadow-xl hover:border-primary/20 transition-all duration-300 rounded-3xl overflow-hidden group"
                >
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={project.image_url}
                      alt={project.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute top-4 right-4">
                      <span className="bg-secondary text-secondary-foreground px-4 py-2 rounded-full text-sm font-medium capitalize">
                        {project.category}
                      </span>
                    </div>
                  </div>

                  <div className="p-6">
                    <h3 className="font-heading font-semibold text-xl mb-3 text-primary">
                      {project.title}
                    </h3>
                    <p className="text-muted-foreground mb-4 leading-relaxed">
                      {project.description}
                    </p>

                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <MapPin className="w-4 h-4 text-secondary" />
                        <span>{project.location}</span>
                      </div>

                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Zap className="w-4 h-4 text-secondary" />
                        <span>{project.capacity}</span>
                      </div>

                      <div className="text-muted-foreground">
                        <span className="font-medium">Completed:</span> {project.completion_date}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <p className="text-xl text-muted-foreground">
                No projects found in this category.
              </p>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Projects;
